import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { PortProtocol, portProtocolLabel } from '@/consts/machine'
import { Instance } from '@/models/instance'
import { formatDatePreset } from '@/utils/formatter'

interface InstanceConfigProps {
  instance: Instance
}

export function InstanceConfig({ instance }: InstanceConfigProps) {
  const { t } = useTranslation()

  const snapshotInfos = useMemo(() => {
    if (!instance.fromSnapshot) return []

    const { createTime, subject } = instance.fromSnapshot

    const result = [
      {
        name: 'Snapshot',
        value: subject,
      },
    ]
    const createTimeStr = formatDatePreset(createTime.seconds * 1000, 'minute')

    result.push({
      name: t('instance.detail.config.snapshot'),
      value: `Created at：${createTimeStr}`,
    })

    return result
  }, [instance])

  const imageInfos = useMemo(() => {
    const { image } = instance.rentInfos

    const result = [
      {
        name: t('instance.detail.config.image'),
        value: image.alias,
      },
    ]

    if (image.description) {
      result.push({
        name: t('instance.detail.config.imageDesc'),
        value: image.description,
      })
    }

    return result
  }, [instance])

  const rentInfos = useMemo(() => {
    const { cmd, mountInfos, ports } = instance.rentInfos

    const result = []

    if (mountInfos && mountInfos.length > 0) {
      const isOverTwo = mountInfos.length > 1

      for (let i = 0; i < mountInfos.length; i += 1) {
        const mountInfo = mountInfos[i]
        result.push({
          name: isOverTwo ? `Mount point ${i + 1}` : 'Mount point',
          key: `mount-${i}`,
          value: `${mountInfo.source}:${mountInfo.dest}`,
        })
      }
    }

    if (cmd) {
      result.push({
        name: t('instance.detail.config.cmd'),
        key: 'cmd',
        value: cmd,
      })
    }

    if (ports.length > 0) {
      const protsVal = ports
        .map((p) => `${portProtocolLabel[p.protocol as PortProtocol] || 'unknown'}/${p.srcPort}`)
        .join(', ')

      result.push({
        name: t('instance.detail.config.port'),
        key: 'ports',
        value: protsVal,
      })
    }

    return result
  }, [instance])

  return (
    <div className="bg-panel-gray-100 rounded-lg flex flex-wrap gap-y-4 py-4 t-sm text-tertiary">
      {imageInfos.map((img, idx) => (
        <div className="w-1/2 px-4" key={`img-${idx}`}>
          <div className="text-secondary">{img.name}</div>
          <div className="text-primary">{img.value}</div>
        </div>
      ))}

      {snapshotInfos.map((snap, idx) => (
        <div className="w-1/2 px-4" key={`snap-${idx}`}>
          <div className="text-secondary">{snap.name}</div>
          <div className="text-primary">{snap.value}</div>
        </div>
      ))}

      {rentInfos.map((info, idx) => (
        <div className="w-1/2 px-4" key={`info-${idx}`}>
          <div className="text-secondary">{info.name}</div>
          <div className="text-primary">{info.value}</div>
        </div>
      ))}
    </div>
  )
}
